import _ from 'lodash'
import moment from 'moment'
import * as React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Card, CardBody, Row, Col } from 'reactstrap'

import { PlanUpdateModeTypes } from 'api/plans'
import type { UpdatePlanBulk, WorkersPlan } from 'api/plans'

import { getWorkspaceSummary, selectDashboardStatus } from 'slices/dashboardSlice'
import { selectPlansStatus, updatePlanBulkCreate } from 'slices/plansSlice'
import { getScheduleTypeList, selectScheduleTypesStatus } from 'slices/scheduleTypesSlice'
import { selectSessionStatus } from 'slices/sessionSlice'
import { getWorkspaceList, selectWorkspacesStatus } from 'slices/workspacesSlice'

import EditChangesDiscardDialog from 'components/EditChangesDiscardDialog/EditChangesDiscardDialog'
import { BadgeLabel, WorksSelector, MultipleFooter, WorkerPopover, SubmitFooter, TimeSelect } from 'components/common'
import type { ColorType, SelectedScheduleType } from 'components/common/types'
import { UNSELECTED_SCHEDULE_TYPE_ID } from 'components/common/utils'

import useAssignment from 'hooks/useAssignment'
import useBusinessTime from 'hooks/useBusinessTime'
import usePlans from 'hooks/usePlans'

import TeamWorkerCard from './TeamWorkerCard'
import TeamWorkerReassign from './TeamWorkerReassign'
import { getWorkerPerformance } from './utils'

import styles from './TeamAssignment.module.scss'

import type { ScheduleEditType } from './TeamWorkerReassign'

const TeamAssignment: React.FC = () => {
  const [focusId, setFocusId] = React.useState<number | undefined>()
  const [selectorOpen, setSelectorOpen] = React.useState(false)
  const [multipleMode, setMultipleMode] = React.useState(false)
  const [selectedWorker, setSelectedWorker] = React.useState<number[]>([])
  const [initEditData, setInitEditData] = React.useState<ScheduleEditType[]>([])
  const [editData, setEditData] = React.useState<ScheduleEditType[]>([])
  const [currentScheduleType, setCurrentScheduleType] = React.useState<SelectedScheduleType | undefined>()
  const [selectedScheduleType, setSelectedScheduleType] = React.useState<SelectedScheduleType | undefined>()
  const [openReassign, setOpenReassign] = React.useState(false)
  const [editChangesDiscardDialogOpen, setEditChangesDiscardDialogOpen] = React.useState(false)

  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const {
    team: { groupId },
  } = useSelector(selectSessionStatus, shallowEqual)
  const { workspaceSummary } = useSelector(selectDashboardStatus, shallowEqual)
  const { partialScheduleTypes } = useSelector(selectScheduleTypesStatus, shallowEqual)
  const { partialWorkspaces } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { plans } = useSelector(selectPlansStatus, shallowEqual)

  const {
    startHour,
    startMinute,
    openMoment,
    workspaceId,
    setSubmitted,
    getSubmitWorkerPlanFromWorker,
    initTeamSchedules,
  } = useAssignment()
  const { planWorkDate, planLastUpdatedAt, planLastUpdater } = usePlans()

  const { businessStartTime, businessEndTime } = useBusinessTime()

  React.useEffect(() => {
    dispatch(getWorkspaceList())
  }, [dispatch])

  React.useEffect(() => {
    if (!workspaceId) {
      return
    }
    dispatch(getWorkspaceSummary(workspaceId, moment().format('YYYY-MM-DD')))
    dispatch(getScheduleTypeList(workspaceId))
  }, [dispatch, workspaceId])

  React.useEffect(() => {
    setInitEditData(initTeamSchedules)
    setEditData(initTeamSchedules)
  }, [initTeamSchedules])

  const onCancel = () => {
    setEditData(initEditData)
    setEditChangesDiscardDialogOpen(false)
  }
  const onSubmit = () => {
    if (!plans || !workspaceId) {
      return
    }

    setSubmitted(true)
    const compare = initEditData.flatMap(s => s.workers)
    const data: WorkersPlan[] = editData
      .filter(edit => edit.scheduleTypeId !== UNSELECTED_SCHEDULE_TYPE_ID && edit.name !== '予定未入力')
      .flatMap(edit => {
        return edit.workers
          .filter(worker => !_.isEqual(_.find(compare, ['workerId', worker.workerId]), worker))
          .flatMap(worker => getSubmitWorkerPlanFromWorker(worker, edit.scheduleTypeId, !edit?.color))
      })

    const updatePlanDate: UpdatePlanBulk = {
      updateMode: PlanUpdateModeTypes.WorkPlan,
      workersPlan: data,
    }
    dispatch(updatePlanBulkCreate(workspaceId, planWorkDate, updatePlanDate))
  }

  const onPopoverOpen = (workerId: number, scheduleTypeId: number, name: string) => {
    setFocusId(workerId)
    setSelectedWorker([workerId])
    setCurrentScheduleType({ id: scheduleTypeId, name })
  }

  const onPopoverClose = () => {
    setFocusId(undefined)
  }

  const initialSelected = () => {
    setSelectorOpen(false)
    setMultipleMode(false)
    setSelectedWorker([])
    setCurrentScheduleType(undefined)
  }

  const onSelectorWorkClick = (scheduleTypeId: number, name: string, color?: ColorType) => {
    setSelectedScheduleType({ id: scheduleTypeId, name, color: color })
    setOpenReassign(true)
  }

  const onWorkerCardClick = (workerId: number, scheduleTypeId: number, name: string) => {
    const sameScheduleType = currentScheduleType?.id === scheduleTypeId
    if (sameScheduleType && selectedWorker.includes(workerId)) {
      setSelectedWorker(selectedWorker.filter(id => id !== workerId))
    } else if (sameScheduleType) {
      setSelectedWorker([...selectedWorker, workerId])
    } else {
      setCurrentScheduleType({ id: scheduleTypeId, name })
      setSelectedWorker([workerId])
    }
  }

  const onReassignClose = () => {
    initialSelected()
    setOpenReassign(false)
  }
  const onMultipleButtonClick = () => {
    setSelectedWorker([])
    setMultipleMode(!multipleMode)
  }

  const unchanged = React.useMemo(() => _.isEqual(initEditData, editData), [editData, initEditData])

  const sortSchedule = (a: ScheduleEditType, b: ScheduleEditType) => {
    // workersの数でソートする
    const aHasWorkers = a.workers.length > 0
    const bHasWorkers = b.workers.length > 0
    // 予定未入力にworkersがいる場合は必ず先頭に来るようにする
    if (a.name === '予定未入力' && aHasWorkers) {
      return -1
    }
    if (b.name === '予定未入力' && bHasWorkers) {
      return 1
    }

    // workersがいる場合は先、いない場合は後に来るようにする
    if (aHasWorkers && !bHasWorkers) {
      return -1
    }
    if (!aHasWorkers && bHasWorkers) {
      return 1
    }

    return 0
  }

  const sortedData = React.useMemo(() => editData.sort(sortSchedule), [editData])

  return (
    <>
      <div className={styles.container}>
        <div className="d-flex align-items-center">
          <div className="font-x-large fw-bold flex-grow-1">人員配置</div>
          <div className="bg-white rounded">
            <Button outline onClick={() => navigate(`/team-schedules/${moment().format('YYYY-MM-DD')}`)}>
              本日の作業計画
            </Button>
          </div>
          <div className="bg-white rounded ms-3">
            <Button
              outline
              className="d-flex align-items-center"
              disabled={multipleMode}
              onClick={onMultipleButtonClick}
            >
              <i className="icf-edit pe-2 font-large" />
              <div className="ps-1">まとめて配置変更</div>
            </Button>
          </div>
        </div>
        <div className="d-flex align-items-center pt-3">
          <div className="pe-2">表示中の配置:</div>
          <TimeSelect
            hour={startHour}
            minute={startMinute}
            label=""
            start={businessStartTime}
            end={businessEndTime}
            onChange={(hour, minute) => {
              navigate(`?hour=${hour}&minute=${minute}`, { replace: true })
            }}
          />
          <div className="pe-2">からの配置</div>
          <Button color="link" className="text-decoration-none" onClick={() => navigate(pathname, { replace: true })}>
            <div>現在時刻の配置にする</div>
          </Button>
        </div>
        {sortedData.map((type, index) => (
          <Card key={`card-${type.scheduleTypeId}-${index}`} className="my-3 shadow-sm">
            <CardBody className="p-3">
              <div className="d-flex mb-2">
                <BadgeLabel label={type.name} color={type.color} />
              </div>
              <Row sm={4} className="mx-0">
                {type.workers.map((worker, i) => (
                  <Col key={`col-${worker.workerId}`} className={`pt-2 pe-0 ps-${i % 4 !== 0 ? '2' : '0'}`}>
                    {multipleMode ? (
                      <TeamWorkerCard
                        workerId={worker.workerId}
                        workerName={worker.workerName}
                        groupName={worker.groupName}
                        groupColor={worker.groupColor}
                        status={selectedWorker.includes(worker.workerId) ? 'selected' : worker.status}
                        performance={getWorkerPerformance(workspaceSummary, groupId, worker.workerId, openMoment)}
                        onClick={() => onWorkerCardClick(worker.workerId, type.scheduleTypeId, type.name)}
                      />
                    ) : (
                      <WorkerPopover
                        startAt={worker.startAt}
                        duration={worker.duration}
                        scheduleType={{
                          id: type.scheduleTypeId,
                          name: type.name,
                          color: type?.color,
                        }}
                        offsetY={-45}
                        onClick={() => setSelectorOpen(true)}
                        onOpen={() => onPopoverOpen(worker.workerId, type.scheduleTypeId, type.name)}
                        onClose={onPopoverClose}
                      >
                        <TeamWorkerCard
                          workerId={worker.workerId}
                          workerName={worker.workerName}
                          groupName={worker.groupName}
                          groupColor={worker.groupColor}
                          status={worker.status}
                          isFocus={focusId === worker.workerId}
                          performance={getWorkerPerformance(workspaceSummary, groupId, worker.workerId, openMoment)}
                        />
                      </WorkerPopover>
                    )}
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Card>
        ))}
      </div>
      <TeamWorkerReassign
        isOpen={openReassign}
        selectedWorkerIds={selectedWorker}
        selectedScheduleType={selectedScheduleType}
        currentScheduleType={currentScheduleType}
        editData={editData}
        currentTime={openMoment.format('HH:mm')}
        setEditData={setEditData}
        onClose={onReassignClose}
        getWorkerPerformance={workerId => getWorkerPerformance(workspaceSummary, groupId, workerId, openMoment)}
      />
      <WorksSelector
        open={selectorOpen}
        scheduleTypes={partialScheduleTypes}
        workspaces={partialWorkspaces}
        currentScheduleType={currentScheduleType}
        workspaceId={workspaceId ?? 0}
        showWorkspace={true}
        onClose={() => setSelectorOpen(!selectorOpen)}
        onWorkClick={onSelectorWorkClick}
      />
      {multipleMode && (
        <MultipleFooter
          stepNumber={selectorOpen ? 2 : 1}
          stepText={selectorOpen ? '作業内容を選択' : 'スケジュールを変更したいユーザーを選択'}
          disabled={selectedWorker.length === 0}
          selectorOpen={selectorOpen}
          decisionButtonClick={() => setSelectorOpen(true)}
          onCancel={initialSelected}
        />
      )}
      {!multipleMode && !unchanged && (
        <SubmitFooter
          onCancel={() => setEditChangesDiscardDialogOpen(true)}
          onSubmit={onSubmit}
          updatedBy={planLastUpdater}
          updatedAt={planLastUpdatedAt}
        />
      )}

      <EditChangesDiscardDialog
        isOpen={editChangesDiscardDialogOpen}
        onCancel={() => setEditChangesDiscardDialogOpen(false)}
        onDiscard={onCancel}
      />
    </>
  )
}

export default TeamAssignment
